import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

/**
 * Performance / appreciation carousel.
 * - Renders 2 tiles per "slide" (1 on mobile)
 * - Auto-rotates every `intervalMs` (default 8s) with a soft cross-fade
 * - Pauses on hover and via a manual pause/play toggle
 * - Prev/Next buttons + pagination dots
 *
 * `variant`:
 *   - 'light'  : white tile on cream background (Home page)
 *   - 'dark'   : translucent tile on ink background (About page)
 */
export default function PerformanceCarousel({ tiles, intervalMs = 8000, variant = 'light' }) {
  const tilesPerSlide = useResponsiveCount();
  const slides = chunk(tiles, tilesPerSlide);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef(null);

  const next = useCallback(() => setIdx((i) => (i + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + slides.length) % slides.length), [slides.length]);

  useEffect(() => {
    if (paused || hovered || slides.length <= 1) return undefined;
    timerRef.current = setInterval(next, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [paused, hovered, next, intervalMs, slides.length]);

  // Clamp idx if tilesPerSlide changes on resize (derive on render — no effect needed)
  const safeIdx = slides.length === 0 ? 0 : Math.min(idx, slides.length - 1);

  const isDark = variant === 'dark';

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="performance-carousel"
    >
      {/* Tile grid */}
      <div className="relative">
        {slides.map((group, si) => (
          <div
            key={si}
            className={`grid lg:grid-cols-2 gap-5 lg:gap-6 transition-opacity duration-700 ${
              si === safeIdx ? 'opacity-100 relative' : 'opacity-0 absolute inset-0 pointer-events-none'
            }`}
            aria-hidden={si !== safeIdx}
            data-testid={`performance-slide-${si}`}
          >
            {group.map((tile, ti) => (
              <Tile
                key={tile.id}
                tile={tile}
                isDark={isDark}
                animateIn={si === safeIdx}
                delayMs={ti * 80}
              />
            ))}
            {/* If the last slide is short, render an empty cell so the grid stays balanced */}
            {group.length < tilesPerSlide &&
              Array.from({ length: tilesPerSlide - group.length }).map((_, k) => (
                <div key={`pad-${k}`} className="hidden lg:block"/>
              ))}
          </div>
        ))}
      </div>

      {/* Controls */}
      {slides.length > 1 && (
        <div
          className="mt-8 flex items-center justify-between gap-4"
          data-testid="performance-carousel-controls"
        >
          {/* Dots */}
          <div className="flex items-center gap-2" role="tablist" aria-label="Testimonial pagination">
            {slides.map((_, si) => (
              <button
                key={si}
                type="button"
                role="tab"
                aria-selected={si === safeIdx}
                aria-label={`Show testimonial group ${si + 1}`}
                onClick={() => setIdx(si)}
                data-testid={`performance-dot-${si}`}
                className={`transition-all duration-300 rounded-full ${
                  si === safeIdx
                    ? 'w-10 h-3 bg-[var(--orange)]'
                    : isDark
                      ? 'w-3 h-3 bg-white/40 hover:bg-white/80'
                      : 'w-3 h-3 bg-[var(--ink)]/45 hover:bg-[var(--orange)]'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <CtrlBtn
              isDark={isDark}
              onClick={() => setPaused((p) => !p)}
              ariaLabel={paused ? 'Resume auto-rotation' : 'Pause auto-rotation'}
              testid="performance-pause-toggle"
            >
              {paused ? <Play className="w-4 h-4"/> : <Pause className="w-4 h-4"/>}
            </CtrlBtn>
            <CtrlBtn isDark={isDark} onClick={prev} ariaLabel="Previous testimonials" testid="performance-prev">
              <ChevronLeft className="w-4 h-4"/>
            </CtrlBtn>
            <CtrlBtn isDark={isDark} onClick={next} ariaLabel="Next testimonials" testid="performance-next">
              <ChevronRight className="w-4 h-4"/>
            </CtrlBtn>
          </div>
        </div>
      )}
    </div>
  );
}

function CtrlBtn({ children, onClick, ariaLabel, testid, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testid}
      className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${
        isDark
          ? 'border-white/20 text-white/85 hover:border-[var(--orange)] hover:text-[var(--orange)]'
          : 'border-[var(--line)] text-[var(--ink)] hover:border-[var(--orange)] hover:text-[var(--orange)] bg-white'
      }`}
    >
      {children}
    </button>
  );
}

function Tile({ tile, isDark, animateIn, delayMs }) {
  return (
    <article
      className={`group relative rounded-3xl p-8 md:p-10 flex flex-col transition-all duration-500 ${
        isDark
          ? 'bg-white/[0.04] border border-white/10 hover:bg-white/[0.08]'
          : 'bg-white border border-[var(--line)] hover:border-[var(--orange)]/40 hover-lift'
      } ${animateIn ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`}
      style={{ transitionDelay: animateIn ? `${delayMs}ms` : '0ms' }}
      data-testid={`performance-tile-${tile.id}`}
    >
      {/* Header — stars + badge */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex gap-1">
          {[...Array(5)].map((_, k) => (
            <Star
              key={k}
              className={`w-4 h-4 fill-[var(--orange)] ${isDark ? 'text-[var(--blue)]' : 'text-[var(--orange)]'}`}
            />
          ))}
        </div>
        <span
          className={`text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1.5 rounded-full ${
            isDark
              ? 'text-[var(--blue)] bg-[var(--orange)]/12'
              : 'text-[var(--orange)] bg-[var(--orange)]/12'
          }`}
        >
          {tile.badge}
        </span>
      </div>

      <Quote className={`w-10 h-10 mb-4 ${isDark ? 'text-[var(--blue)]/25' : 'text-[var(--orange)]/25'}`}/>

      <p
        className={`text-[15px] md:text-[16.5px] leading-[1.85] flex-1 quote-body ${
          isDark ? 'text-white/85' : 'text-[var(--text-mid)]'
        }`}
        dangerouslySetInnerHTML={{ __html: tile.quoteHtml }}
      />

      {/* Footer — signature + logo bottom-left */}
      <div className={`mt-8 pt-6 flex items-end justify-between gap-5 border-t ${isDark ? 'border-white/10' : 'border-[var(--line)]'}`}>
        {/* Logo bottom-left — large, visual */}
        <div
          className={`w-40 md:w-44 h-24 rounded-2xl bg-white flex items-center justify-center p-3 shrink-0 border ${
            isDark ? 'border-white/15 shadow-lg' : 'border-[var(--line)]'
          }`}
          data-testid={`performance-tile-logo-${tile.id}`}
        >
          <img
            src={tile.logo}
            alt={tile.company}
            className="max-h-20 max-w-[150px] w-auto h-auto object-contain"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
          />
        </div>

        {/* Signature */}
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex-1 min-w-0 text-right">
            <div className={`font-display font-bold text-[16px] md:text-[17px] truncate ${isDark ? 'text-white' : 'text-[var(--ink)]'}`}>
              {tile.person}
            </div>
            <div className={`text-[12px] mt-0.5 truncate ${isDark ? 'text-white/55' : 'text-[var(--text-mid)]'}`}>
              {tile.role}
            </div>
            <div className={`text-[12px] mt-0.5 font-semibold uppercase tracking-wider truncate ${isDark ? 'text-[var(--blue)]' : 'text-[var(--orange)]'}`}>
              {tile.company}
            </div>
          </div>
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${tile.initialsGradient} flex items-center justify-center shrink-0 ring-2 ${isDark ? 'ring-white/15' : 'ring-white'}`}>
            <span className="font-display font-bold text-[15px] text-white">{tile.initials}</span>
          </div>
        </div>
      </div>

      {/* Local style for italic emphasis inside the quote */}
      <style>{`
        [data-testid="performance-tile-${tile.id}"] .quote-body em {
          font-style: normal;
          color: ${isDark ? 'var(--blue)' : 'var(--orange)'};
          font-weight: 600;
        }
        [data-testid="performance-tile-${tile.id}"] .quote-body b {
          color: ${isDark ? '#fff' : 'var(--ink)'};
        }
      `}</style>
    </article>
  );
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function useResponsiveCount() {
  const get = () => (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches ? 2 : 1);
  const [n, setN] = useState(get);
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const handler = () => setN(get());
    if (m.addEventListener) m.addEventListener('change', handler);
    else m.addListener(handler);
    return () => {
      if (m.removeEventListener) m.removeEventListener('change', handler);
      else m.removeListener(handler);
    };
  }, []);
  return n;
}
